/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import { useTranslation } from 'react-i18next';
import { Text, UnstyledButton } from '@/shared/ui';
import { editBtnStyle, tableEmptyStyle } from './settings-block-styles';

/** The catch-all every uncategorized document falls back to; it cannot be renamed. */
export const FREE_LABEL_CATEGORY_ID = 'other';

interface Category {
  id: string;
}

interface DocumentCategoryTilesProps<C extends Category> {
  categories: C[];
  labelOf: (category: C) => string;
  canManage: boolean;
  onRename: (category: C) => void;
  emptyText: string;
  testId: string;
}

/**
 * A tab's document categories as a grid of small tiles, name and rename side
 * by side. A one-word name on a full-width table row wasted most of the row,
 * and seven of them pushed everything else off the screen.
 *
 * @returns The tile grid, or the empty line.
 */
export function DocumentCategoryTiles<C extends Category>({
  categories, labelOf, canManage, onRename, emptyText, testId,
}: DocumentCategoryTilesProps<C>) {
  const { t } = useTranslation('features/agency-settings');

  if (categories.length === 0) return <div style={tableEmptyStyle}>{emptyText}</div>;

  return (
    <div
      data-testid={testId}
      className="grid gap-(--space-2)"
      style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))' }}
    >
      {categories.map((category) => {
        const label = labelOf(category);
        const fixed = category.id === FREE_LABEL_CATEGORY_ID;
        return (
          <div
            key={category.id}
            className="flex items-center justify-between gap-(--space-2)"
            style={{
              minHeight: 44, padding: 'var(--space-1) var(--space-2) var(--space-1) var(--space-3)',
              border: '1px solid var(--border-subtle)', borderRadius: 'var(--radius-md)',
              background: 'var(--surface-primary)',
            }}
          >
            <Text component="span" style={{ minWidth: 0, fontSize: 'var(--text-body-sm)', fontWeight: 600, color: 'var(--text-primary)' }}>
              {label}
            </Text>
            {fixed && (
              <Text component="span" style={{ fontSize: 'var(--text-meta)', color: 'var(--text-tertiary-soft)' }}>
                {t('v2.items.categoryFixed')}
              </Text>
            )}
            {!fixed && canManage && (
              <UnstyledButton
                type="button"
                onClick={() => onRename(category)}
                style={editBtnStyle}
                aria-label={t('v2.items.renameCategory', { name: label })}
              >
                {t('v2.items.rename')}
              </UnstyledButton>
            )}
          </div>
        );
      })}
    </div>
  );
}
